import { make } from "./dom";

/** One capture keeps its identity and action as it moves from featured to older. */
export interface FindCardModel {
  name: string;
  printingLabel: string;
  ambiguous: boolean;
  resolved?: boolean;
  /** "user" for a review choice, "price" for the price-tie policy; the tag says which. */
  resolvedBy?: "user" | "price";
  prices: readonly string[];
  priceNotes?: readonly string[];
  preview: () => HTMLCanvasElement | HTMLImageElement | null;
  open: () => void;
}

function corner(): HTMLElement {
  const marker = make("span", "amb-corner");
  marker.setAttribute("aria-hidden", "true");
  return marker;
}

function capture(find: FindCardModel): HTMLElement {
  const surface = make("span", "capture");
  surface.setAttribute("aria-hidden", "true");
  const image = find.preview();
  if (image) {
    image.className = "find-capture-image";
    surface.append(image);
  }
  return surface;
}

function price(find: FindCardModel): HTMLElement {
  const value = make("span", "price");
  value.append(...find.prices.map((label) => make("span", "price-amount", label)));
  return value;
}

function openButton(find: FindCardModel, className: string): HTMLButtonElement {
  const button = make("button", `${className} find-open`);
  button.type = "button";
  button.classList.toggle("is-ambiguous", find.ambiguous);
  button.classList.toggle("is-resolved", !!find.resolved);
  if (find.resolved) button.dataset.resolvedBy = find.resolvedBy ?? "user";
  const resolvedNote = !find.resolved
    ? ""
    : find.resolvedBy === "price"
      ? ", settled by price"
      : ", chosen by you";
  button.setAttribute(
    "aria-label",
    `Open scan of ${find.name}, ${find.ambiguous ? "ambiguous" : find.printingLabel}${resolvedNote}. ` +
      (find.prices[0] === "—" ? "Price unavailable." : `${find.prices.join("; ")}.`) +
      (find.priceNotes?.length ? ` ${find.priceNotes.join(". ")}.` : ""),
  );
  button.addEventListener("click", find.open);
  return button;
}

function identity(find: FindCardModel): HTMLElement {
  const body = make("span", "body");
  const name = make("span", "name", find.name);
  if (!/\s/.test(find.name)) name.classList.add("card-name--single");
  const printing = make("span", "set", find.ambiguous ? "ambiguous" : find.printingLabel);
  if (find.resolved) {
    const byPrice = find.resolvedBy === "price";
    const tag = make(
      "span",
      byPrice ? "find-yours find-by-price" : "find-yours",
      byPrice ? "by price" : "yours",
    );
    if (byPrice)
      tag.title =
        "Printings tied on match and were worth about the same; the more valuable one was taken";
    printing.append(" · ", tag);
  }
  body.append(name, printing);
  return body;
}

export function featuredFind(find: FindCardModel): HTMLButtonElement {
  const button = openButton(find, "find-featured");
  if (find.ambiguous) button.append(corner());
  button.append(capture(find), identity(find), price(find));
  return button;
}

/** Preserve the button and any modal's return-focus reference when a newer find arrives. */
export function demoteFind(button: HTMLButtonElement): void {
  button.classList.remove("find-featured");
  button.classList.add("find-row", "is-older");
  button.style.animation = "none";
}
